/**
 * Task 279 — pure helpers for the Client Operating Setup panel.
 *
 * No React here: form-state mapping, validation, and payload building are kept
 * pure so they can be unit-tested directly (repo convention — pure-fn + source-
 * grep, no @testing-library/react). The panel component imports these.
 */

import type {
  ClientOperatingSetup,
  ClientPackageAssignmentRow,
  ContentVariantPlatform,
  PackageServiceDeliverable,
  ServiceCadence,
  UpsertClientOperatingSetupInput,
} from '../../api/types';

export const PER_WEEK_MIN = 0;
export const PER_WEEK_MAX = 14;
export const LEAD_BUFFER_MIN = 0;
export const LEAD_BUFFER_MAX = 60;

export const HHMM_REGEX = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
// Same IANA shape the backend (calendar draft RPC + operating-setup CHECK) uses.
export const IANA_TZ_REGEX = /^(UTC|[A-Za-z_]+\/[A-Za-z_+-]+(\/[A-Za-z_+-]+)?)$/;
export const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// Task 280K — product defaults for the initial scheduling fields. Applied in
// `formStateFromRow` so a new/blank setup starts with sensible values instead of
// an empty timezone (which used to surface a "required" error) or an empty,
// ambiguously-formatted times field.
export const DEFAULT_TIMEZONE = 'Asia/Riyadh';
export const DEFAULT_PREFERRED_TIMES_TEXT = '09:00, 16:00';

/**
 * Curated timezone options for the operating-setup select (all valid under
 * `IANA_TZ_REGEX`). A loaded row whose timezone is not in this list still
 * displays via a fallback option in the component.
 */
export const TIMEZONE_OPTIONS: ReadonlyArray<{ readonly value: string; readonly label: string }> = [
  { value: 'Asia/Riyadh', label: 'الرياض' },
  { value: 'Asia/Dubai', label: 'دبي' },
  { value: 'Asia/Kuwait', label: 'الكويت' },
  { value: 'Asia/Qatar', label: 'الدوحة' },
  { value: 'Asia/Bahrain', label: 'المنامة' },
  { value: 'Asia/Amman', label: 'عمّان' },
  { value: 'Africa/Cairo', label: 'القاهرة' },
  { value: 'Europe/London', label: 'لندن' },
  { value: 'Etc/UTC', label: 'التوقيت العالمي (UTC)' },
];

/** ISO weekday 1=Mon … 7=Sun, with Arabic labels. */
export const DAY_ITEMS: ReadonlyArray<{ readonly key: number; readonly label: string }> = [
  { key: 1, label: 'الإثنين' },
  { key: 2, label: 'الثلاثاء' },
  { key: 3, label: 'الأربعاء' },
  { key: 4, label: 'الخميس' },
  { key: 5, label: 'الجمعة' },
  { key: 6, label: 'السبت' },
  { key: 7, label: 'الأحد' },
];

/** The seven supported publish targets (publish_target_v1). */
export const PLATFORM_ITEMS: ReadonlyArray<{
  readonly key: ContentVariantPlatform;
  readonly label: string;
}> = [
  { key: 'instagram_feed', label: 'إنستغرام · منشور' },
  { key: 'instagram_story', label: 'إنستغرام · ستوري' },
  { key: 'instagram_reel', label: 'إنستغرام · ريلز' },
  { key: 'facebook_post', label: 'فيسبوك · منشور' },
  { key: 'facebook_story', label: 'فيسبوك · ستوري' },
  { key: 'tiktok_video', label: 'تيك توك · فيديو' },
  { key: 'snapchat_story', label: 'سناب شات · ستوري' },
];

const PLATFORM_KEYS: ReadonlyArray<string> = PLATFORM_ITEMS.map((p) => p.key);

export function dayLabel(key: number): string {
  return DAY_ITEMS.find((d) => d.key === key)?.label ?? String(key);
}
export function platformLabel(key: string): string {
  return PLATFORM_ITEMS.find((p) => p.key === key)?.label ?? key;
}

export interface OperatingSetupFormState {
  postsPerWeek: string;
  staticDesignsPerWeek: string;
  reelsVideosPerWeek: string;
  platforms: ContentVariantPlatform[];
  firstPublishDate: string; // YYYY-MM-DD or ''
  preferredDays: number[]; // 1..7
  preferredTimesText: string; // comma-separated HH:MM
  timezone: string;
  productionLeadTimeDays: string;
  approvalBufferDays: string;
  isActive: boolean;
}

function numToStr(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

/**
 * comma-separated HH:MM text → trimmed non-empty tokens. Accepts BOTH the
 * English comma `,` and the Arabic comma `،` (Task 280K) as separators, since
 * the field placeholder shows the Arabic comma to Arabic-RTL users.
 */
export function parseTimesInput(raw: string): string[] {
  return raw
    .split(/[,،]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

export function formatTimesInput(times: ReadonlyArray<string> | null | undefined): string {
  return (times ?? []).join(', ');
}

/** True for a real YYYY-MM-DD calendar date (rejects e.g. 2026-02-30). */
export function isValidIsoDate(value: string): boolean {
  if (!DATE_REGEX.test(value)) return false;
  const ms = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(ms)) return false;
  const d = new Date(ms);
  const round = `${d.getUTCFullYear().toString().padStart(4, '0')}-${(d.getUTCMonth() + 1)
    .toString()
    .padStart(2, '0')}-${d.getUTCDate().toString().padStart(2, '0')}`;
  return round === value;
}

/** Build editable form state from a loaded row (or a blank form when null). */
export function formStateFromRow(row: ClientOperatingSetup | null): OperatingSetupFormState {
  return {
    postsPerWeek: numToStr(row?.posts_per_week),
    staticDesignsPerWeek: numToStr(row?.static_designs_per_week),
    reelsVideosPerWeek: numToStr(row?.reels_videos_per_week),
    platforms: row?.platforms ? [...row.platforms] : [],
    firstPublishDate: row?.first_publish_date ?? '',
    preferredDays: row?.preferred_days ? [...row.preferred_days] : [],
    // Task 280K — default to 09:00, 16:00 ONLY when no times value is set
    // (null/undefined). An explicit value — including an explicit empty array —
    // is preserved verbatim (an empty array formats to '' and is NOT promoted to
    // the default, so a deliberately-cleared row is never silently re-filled).
    preferredTimesText:
      row?.preferred_times_local == null
        ? DEFAULT_PREFERRED_TIMES_TEXT
        : formatTimesInput(row.preferred_times_local),
    // Task 280K — default to Asia/Riyadh ONLY when no timezone is set
    // (null/undefined), so the form starts valid and never shows the "required"
    // error on first render. An explicit value (even '') is kept as-is, so a
    // blank timezone still serializes back to null rather than being promoted.
    timezone: row?.timezone ?? DEFAULT_TIMEZONE,
    productionLeadTimeDays: row ? String(row.production_lead_time_days) : '',
    approvalBufferDays: row ? String(row.approval_buffer_days) : '',
    isActive: row ? row.is_active : true,
  };
}

/** True when the form sets any of the initial-scheduling-default fields. */
export function usesSchedulingDefaults(form: OperatingSetupFormState): boolean {
  return (
    form.preferredDays.length > 0 ||
    parseTimesInput(form.preferredTimesText).length > 0 ||
    form.firstPublishDate.trim().length > 0
  );
}

/**
 * Validate the form. Returns a field-keyed error bag (keys match the API field
 * names so server validation_errors can merge cleanly). Empty bag = valid.
 * Partial setup is allowed — empty numeric/array fields are fine.
 */
export function validateOperatingSetup(form: OperatingSetupFormState): Record<string, string[]> {
  const errors: Record<string, string[]> = {};

  const intField = (key: string, raw: string, min: number, max: number): void => {
    const t = raw.trim();
    if (t.length === 0) return; // optional
    if (!/^\d+$/.test(t)) {
      errors[key] = ['أدخل رقمًا صحيحًا.'];
      return;
    }
    const n = Number(t);
    if (n < min || n > max) {
      errors[key] = [`القيمة يجب أن تكون بين ${min} و ${max}.`];
    }
  };

  intField('posts_per_week', form.postsPerWeek, PER_WEEK_MIN, PER_WEEK_MAX);
  intField('static_designs_per_week', form.staticDesignsPerWeek, PER_WEEK_MIN, PER_WEEK_MAX);
  intField('reels_videos_per_week', form.reelsVideosPerWeek, PER_WEEK_MIN, PER_WEEK_MAX);
  intField(
    'production_lead_time_days',
    form.productionLeadTimeDays,
    LEAD_BUFFER_MIN,
    LEAD_BUFFER_MAX,
  );
  intField('approval_buffer_days', form.approvalBufferDays, LEAD_BUFFER_MIN, LEAD_BUFFER_MAX);

  for (const d of form.preferredDays) {
    if (!Number.isInteger(d) || d < 1 || d > 7) {
      errors['preferred_days'] = ['أيام النشر يجب أن تكون من 1 (الإثنين) إلى 7 (الأحد).'];
      break;
    }
  }

  const times = parseTimesInput(form.preferredTimesText);
  if (times.some((t) => !HHMM_REGEX.test(t))) {
    errors['preferred_times_local'] = ['استخدم صيغة HH:MM على مدار 24 ساعة، مفصولة بفواصل.'];
  }

  for (const p of form.platforms) {
    if (!PLATFORM_KEYS.includes(p)) {
      errors['platforms'] = ['منصة غير مدعومة.'];
      break;
    }
  }

  const tz = form.timezone.trim();
  if (usesSchedulingDefaults(form) && tz.length === 0) {
    // timezone is required ONLY when initial scheduling defaults are set,
    // otherwise the API's nullable-timezone rule is preserved.
    errors['timezone'] = ['المنطقة الزمنية مطلوبة عند تحديد إعدادات الجدولة المبدئية.'];
  } else if (tz.length > 0 && !IANA_TZ_REGEX.test(tz)) {
    errors['timezone'] = ['منطقة زمنية غير صالحة (مثال: Asia/Riyadh).'];
  }

  const date = form.firstPublishDate.trim();
  if (date.length > 0 && !isValidIsoDate(date)) {
    errors['first_publish_date'] = ['أدخل تاريخًا صالحًا بصيغة YYYY-MM-DD.'];
  }

  return errors;
}

/** Map validated form state to the upsert payload (empty optional → null). */
export function buildOperatingSetupPayload(
  form: OperatingSetupFormState,
  clientId: string,
): UpsertClientOperatingSetupInput {
  const optInt = (raw: string): number | null => {
    const t = raw.trim();
    return t.length === 0 ? null : Number(t);
  };
  const intOrZero = (raw: string): number => {
    const t = raw.trim();
    return t.length === 0 ? 0 : Number(t);
  };
  const tz = form.timezone.trim();
  const date = form.firstPublishDate.trim();
  const times = parseTimesInput(form.preferredTimesText);

  return {
    client_id: clientId,
    // Task 280C — these per-week columns are no longer edited in any panel:
    // package deliverable counts live in the package editor's config, and the
    // derived weekly hint moved there. They are kept here purely as legacy
    // carry-through — a loaded value is re-sent unchanged so a scheduling-only
    // edit never nulls it; a new/blank row simply sends null.
    posts_per_week: optInt(form.postsPerWeek),
    static_designs_per_week: optInt(form.staticDesignsPerWeek),
    reels_videos_per_week: optInt(form.reelsVideosPerWeek),
    platforms: form.platforms.length > 0 ? [...form.platforms] : null,
    // Dedupe + sort so the stored array is clean (the chip UI cannot produce
    // duplicates, but a loaded row might).
    preferred_days:
      form.preferredDays.length > 0 ? [...new Set(form.preferredDays)].sort((a, b) => a - b) : null,
    preferred_times_local: times.length > 0 ? times : null,
    timezone: tz.length > 0 ? tz : null,
    first_publish_date: date.length > 0 ? date : null,
    production_lead_time_days: intOrZero(form.productionLeadTimeDays),
    approval_buffer_days: intOrZero(form.approvalBufferDays),
    is_active: form.isActive,
  };
}

// ── Task 279B — package-deliverable display (read-only, package-sourced) ──
//
// The "package deliverables" section of the operating-setup panel is a
// read-only summary DERIVED from the client's active package assignment
// (`services_snapshot[].deliverables[]`) — not editable scheduling input. These
// Arabic label maps mirror `ClientServicesPage`'s private `deliverableTypeLabel`
// / `serviceCadenceLabel`; the duplication is an accepted V1 isolation
// trade-off (a follow-up may extract a shared labels module).

const DELIVERABLE_TYPE_LABELS: Readonly<Record<PackageServiceDeliverable['type'], string>> = {
  design: 'تصميم',
  post: 'منشور',
  reel: 'ريل',
  story: 'ستوري',
  video: 'فيديو',
  campaign: 'حملة',
  report: 'تقرير',
  meeting: 'اجتماع',
  custom: 'مخصّص',
};

const SERVICE_CADENCE_LABELS: Readonly<Record<ServiceCadence, string>> = {
  one_time: 'مرّة واحدة',
  weekly: 'أسبوعي',
  monthly: 'شهري',
  quarterly: 'ربع سنوي',
  per_campaign: 'لكل حملة',
  custom: 'مخصّص',
};

export function deliverableTypeLabelAr(type: PackageServiceDeliverable['type']): string {
  return DELIVERABLE_TYPE_LABELS[type] ?? type;
}

export function serviceCadenceLabelAr(cadence: ServiceCadence | null): string {
  return cadence ? (SERVICE_CADENCE_LABELS[cadence] ?? '—') : '—';
}

/** A package service with at least one deliverable, ready for read-only display. */
export interface PackageDeliverableGroup {
  readonly serviceId: string;
  readonly serviceName: string;
  readonly deliverables: ReadonlyArray<PackageServiceDeliverable>;
}

/**
 * Flatten a client's active package assignment into the deliverable groups to
 * display read-only. Returns [] when there is no active assignment or no
 * included service carries deliverables — the caller renders a needs-setup
 * state in that case. Pure (no React) so it is unit-tested directly.
 */
export function packageDeliverablesFromAssignment(
  assignment: ClientPackageAssignmentRow | null,
): PackageDeliverableGroup[] {
  if (!assignment) return [];
  return assignment.services_snapshot
    .filter((s) => s.included !== false && s.deliverables.length > 0)
    .map((s) => ({
      serviceId: s.service_id,
      serviceName: s.service_name,
      deliverables: s.deliverables,
    }));
}

// Task 280C — the derived weekly hint moved to the package editor
// (apps/web/src/components/services-packages/socialWeeklyHint.ts), because the
// monthly counts live in the per-service `config` jsonb and are never captured
// in the assignment snapshot's `deliverables[]`. The assignment-based
// derivation that used to live here (280A) read the wrong source and always
// produced 0, so it has been removed.
